import { type FormatNameplateOverflow } from './types.ts';

const MAX_NUMBER_OF_NAMES_TO_DISPLAY = 3;
const MAX_CHARACTERS = 64;

/**
 * Returns the largest prefix of `names` that can be joined with ', ' separators
 * without exceeding `maxChars`, plus the count that had to be dropped.
 */
export function fitNamesWithinLimit(
  names: string[],
  maxChars: number
): { namesThatFit: string[]; numberOfNamesThatDidNotFit: number } {
  let totalLength = 0;
  const included: string[] = [];

  for (let i = 0; i < names.length; i++) {
    const nameLength = names[i].length + (i > 0 ? 2 : 0);

    if (totalLength + nameLength > maxChars) {
      break;
    }

    included.push(names[i]);
    totalLength += nameLength;
  }

  return {
    namesThatFit: included,
    numberOfNamesThatDidNotFit: names.length - included.length
  };
}

/**
 * Builds the nameplate label (e.g. "Alice, Bob, + 2 more") from display names,
 * capping both the number of names and the total character count.
 */
export function buildNameplateText(
  displayNames: string[],
  formatOverflow: FormatNameplateOverflow
): string {
  const firstNParticipants = displayNames.slice(0, MAX_NUMBER_OF_NAMES_TO_DISPLAY);

  const { namesThatFit, numberOfNamesThatDidNotFit } = fitNamesWithinLimit(
    firstNParticipants,
    MAX_CHARACTERS
  );

  const visibleNames = namesThatFit.join(', ');

  const numberOfAdditionalParticipants =
    Math.max(displayNames.length - MAX_NUMBER_OF_NAMES_TO_DISPLAY, 0) +
    numberOfNamesThatDidNotFit;

  if (numberOfAdditionalParticipants > 0) {
    const overflow = `+ ${formatOverflow(numberOfAdditionalParticipants)}`;

    // When no name fits the character budget, `visibleNames` is empty; omit the
    // separator so we don't render a leading ", ".
    return visibleNames ? `${visibleNames}, ${overflow}` : overflow;
  }

  return visibleNames;
}
